import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import {
  FEEXPAY_MIN_AMOUNT,
  computeFeexPayAmount,
  getFeexPayActiveCountries,
  getFeexPayGatewayCurrency,
  isFeexPayAmountValid,
  resolveFeexPayNetworks,
  resolveXofRateFor,
  type FeexPayConfig,
} from '@/lib/feexpay';
import { CURRENCIES, normalizeCurrency } from '@/lib/currency';
import FeexPayModal, { type FeexPayWidgetState } from '@/components/feature/FeexPayModal';
import { readWidgetOutcome } from '@/lib/shopPayment';

// ─────────────────────────────────────────────────────────────
// Recharge du portefeuille Zifek via la MODALE FeexPay.
//
// Exactement le même tunnel que le checkout d'une boutique :
//   1. on prépare la recharge côté serveur (référence + identifiants) ;
//   2. la modale FeexPay s'ouvre ici même (pays, opérateur, téléphone) ;
//   3. dès que le paiement est validé, le solde est crédité et l'écran de
//      succès s'affiche immédiatement — plus de page « paiement en cours ».
// ─────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  userId: number;
  currency: string;
  onClose: () => void;
  onCredited: () => void;
}

type Phase = 'form' | 'preparing' | 'widget' | 'verifying' | 'paid' | 'pending';

const PRESETS = [500, 1000, 2500, 5000];

const formatMoney = (value: number): string =>
  (Number(value) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 });

interface PrepareResult {
  reference: string;
  shopId: string;
  token: string;
  amount: number;
  currency: string;
  gatewayAmount: number;
  gatewayCurrency: string;
}

interface VerifyResult {
  status: 'paid' | 'failed' | 'pending';
  creditedAmount?: number;
  creditedCurrency?: string;
  balance?: number;
  message?: string;
}

export default function WalletTopUpModal({ open, userId, currency, onClose, onCredited }: Props) {
  const { user } = useAuth();
  const [config, setConfig] = useState<FeexPayConfig | null>(null);
  const [payCurrency, setPayCurrency] = useState(() => normalizeCurrency(currency || 'MAD'));
  const [amount, setAmount] = useState<number>(1000);
  const [country, setCountry] = useState('');
  const [network, setNetwork] = useState('');
  const [phase, setPhase] = useState<Phase>('form');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [widget, setWidget] = useState<FeexPayWidgetState | null>(null);
  const [paid, setPaid] = useState<{ amount: number; currency: string; balance: number | null } | null>(null);
  const lastTxnRef = useRef('');
  const widgetOkRef = useRef(false);

  // Réinitialise l'écran à chaque ouverture.
  useEffect(() => {
    if (open) {
      setPayCurrency(normalizeCurrency(currency || 'MAD'));
      setPhase('form');
      setError('');
      setNotice('');
      setWidget(null);
      setPaid(null);
      widgetOkRef.current = false;
      lastTxnRef.current = '';
    }
  }, [open, currency]);

  // Configuration FeexPay (pays / opérateurs activés par le SuperAdmin).
  useEffect(() => {
    if (!open) return;
    let active = true;
    void (async () => {
      const { data } = await supabase
        .from('payment_providers')
        .select('config, public_key, enabled')
        .eq('provider', 'feexpay')
        .maybeSingle();
      if (!active) return;
      const cfg = ((data?.config || {}) as FeexPayConfig) || null;
      setConfig(cfg);
      const countries = getFeexPayActiveCountries(cfg);
      const firstCountry = String(cfg?.country || '') || countries[0]?.code || '';
      setCountry((prev) => prev || firstCountry);
      const nets = resolveFeexPayNetworks(cfg).filter((o) => o.enabled && o.country === firstCountry);
      const defaultNet = String(cfg?.network || '');
      setNetwork((prev) => prev || (nets.some((o) => o.slug === defaultNet) ? defaultNet : nets[0]?.slug || ''));
    })();
    return () => { active = false; };
  }, [open]);

  const countries = useMemo(() => getFeexPayActiveCountries(config), [config]);
  const operators = useMemo(
    () => resolveFeexPayNetworks(config).filter((o) => o.enabled && o.country === country),
    [config, country],
  );

  const gatewayCurrency = getFeexPayGatewayCurrency(country);
  const rate = resolveXofRateFor(payCurrency, config);
  const gatewayAmount = computeFeexPayAmount(amount || 0, rate, payCurrency, undefined, gatewayCurrency);

  const reference = widget?.reference || '';

  const prepare = useCallback(async () => {
    setError('');
    setNotice('');
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Indiquez un montant valide.');
      return;
    }
    if (!country || !network) {
      setError('Choisissez votre pays et votre opérateur Mobile Money.');
      return;
    }
    if (!isFeexPayAmountValid(gatewayAmount)) {
      setError(gatewayAmount < FEEXPAY_MIN_AMOUNT
        ? `Le montant converti (${formatMoney(gatewayAmount)} ${gatewayCurrency}) est inférieur au minimum de ${FEEXPAY_MIN_AMOUNT} ${gatewayCurrency}.`
        : `Le montant converti (${formatMoney(gatewayAmount)} ${gatewayCurrency}) dépasse le maximum autorisé.`);
      return;
    }

    setPhase('preparing');
    try {
      const { data, error: fnError } = await supabase.functions.invoke('zifek-wallet-topup', {
        body: {
          action: 'prepare',
          userId,
          amount,
          currency: payCurrency,
          country,
          network,
          gatewayCurrency,
          method: 'mobile_money',
        },
      });
      if (fnError) {
        let message = 'La recharge n’a pas pu être préparée.';
        const ctx = (fnError as { context?: { json?: () => Promise<{ error?: string }> } }).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const body = await ctx.json();
            if (body?.error) message = body.error;
          } catch { /* message par défaut */ }
        }
        throw new Error(message);
      }
      const res = data as PrepareResult | null;
      if (!res?.shopId || !res?.token) {
        throw new Error('La fenêtre de paiement n’est pas encore configurée. Contactez le support.');
      }
      const operatorLabel = operators.find((o) => o.slug === network)?.label || '';
      setWidget({
        reference: res.reference,
        amount: res.gatewayAmount,
        currency: res.gatewayCurrency,
        shopId: res.shopId,
        token: res.token,
        description: 'Recharge portefeuille Zifek',
        payCase: 'MOBILE',
        firstName: (user?.name || 'Client').split(' ')[0] || 'Client',
        email: user?.email || '',
        phone: '',
        country,
        network: operatorLabel.split(' ')[0] || '',
      });
      setPhase('widget');
    } catch (e) {
      setPhase('form');
      setError(e instanceof Error ? e.message : 'La recharge n’a pas pu être préparée.');
    }
  }, [amount, country, network, gatewayAmount, gatewayCurrency, userId, payCurrency, operators, user]);

  const verify = useCallback(async (transactionReference = '', widgetSuccess = false, silent = false) => {
    if (!reference) {
      setPhase('form');
      setError('Référence de paiement perdue. Recommencez la recharge.');
      return;
    }
    if (!silent) setPhase('verifying');
    setError('');
    try {
      const { data, error: fnError } = await supabase.functions.invoke('zifek-wallet-topup', {
        body: {
          action: 'verify',
          userId,
          reference,
          widgetStatus: widgetSuccess ? 'success' : '',
          transactionReference,
        },
      });
      if (fnError) {
        let message = 'Vérification impossible pour le moment.';
        const ctx = (fnError as { context?: { json?: () => Promise<{ error?: string }> } }).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const body = await ctx.json();
            if (body?.error) message = body.error;
          } catch { /* message par défaut */ }
        }
        throw new Error(message);
      }
      const res = (data || {}) as VerifyResult;
      if (res.status === 'paid') {
        setPaid({
          amount: Number(res.creditedAmount) || amount,
          currency: String(res.creditedCurrency || payCurrency),
          balance: Number.isFinite(Number(res.balance)) ? Number(res.balance) : null,
        });
        setPhase('paid');
        setNotice('');
        onCredited();
        return;
      }
      if (res.status === 'failed') {
        setWidget(null);
        setPhase('form');
        setError(res.message || 'Le paiement a été refusé. Vérifiez votre solde Mobile Money puis réessayez.');
        return;
      }
      setPhase('pending');
      setNotice(res.message || 'Le paiement n’est pas encore confirmé par l’opérateur.');
    } catch (e) {
      setPhase('pending');
      setNotice(e instanceof Error ? e.message : 'Vérification impossible pour le moment.');
    }
  }, [reference, userId, amount, payCurrency, onCredited]);

  const handleWidgetResult = (result: unknown) => {
    const outcome = readWidgetOutcome(result);
    lastTxnRef.current = outcome.transactionReference;
    widgetOkRef.current = outcome.success;
    void verify(outcome.transactionReference, outcome.success);
  };

  // Tant que l'opérateur n'a pas répondu, on réinterroge discrètement.
  useEffect(() => {
    if (phase !== 'pending') return;
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      if (tries > 10) {
        window.clearInterval(timer);
        return;
      }
      void verify(lastTxnRef.current, widgetOkRef.current, true);
    }, 4000);
    return () => window.clearInterval(timer);
  }, [phase, verify]);

  if (!open) return null;

  const busy = phase === 'preparing' || phase === 'verifying';

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-0 sm:px-4">
      <div className="w-full sm:max-w-md bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70 max-h-[92dvh] overflow-y-auto">
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-background-200/70 sticky top-0 bg-background-50">
          <span className="w-9 h-9 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-wallet-3-line"></i>
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold font-heading text-foreground-950">Recharger mon portefeuille</p>
            <p className="text-[11px] text-foreground-500">Paiement sécurisé Mobile Money</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 disabled:opacity-40 transition-colors cursor-pointer"
            aria-label="Fermer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {phase === 'paid' && paid ? (
          <div className="p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-checkbox-circle-fill text-3xl text-accent-600"></i>
            </div>
            <h3 className="text-lg font-bold font-heading text-accent-700">Recharge effectuée</h3>
            <p className="text-sm text-foreground-700 mt-2 leading-relaxed">
              <strong>{formatMoney(paid.amount)} {paid.currency}</strong>{' '}
              ont bien été crédités sur votre portefeuille.
            </p>
            {paid.balance !== null && (
              <div className="mt-4 px-3 py-2.5 rounded-lg bg-background-100 text-xs text-foreground-600">
                Nouveau solde :{' '}
                <strong className="text-foreground-950">
                  {formatMoney(paid.balance)} {paid.currency}
                </strong>
              </div>
            )}
            <p className="mt-3 text-[11px] text-foreground-500 leading-relaxed">
              Paiement confirmé. Le crédit est visible immédiatement dans la devise de votre compte.
            </p>
            <button
              type="button"
              onClick={() => { onCredited(); onClose(); }}
              className="mt-5 w-full py-2.5 rounded-full bg-accent-600 text-background-50 text-sm font-bold hover:bg-accent-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-line mr-1.5"></i>
              Terminer
            </button>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Montant à recharger</label>
              <div className="grid grid-cols-4 gap-1.5 mb-2">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setAmount(p)}
                    disabled={phase !== 'form'}
                    className={`py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 ${
                      amount === p
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-background-200/70'
                    }`}
                  >
                    {p.toLocaleString('fr-FR')}
                  </button>
                ))}
              </div>
              <div className="relative">
                <input
                  type="number"
                  min={1}
                  value={amount || ''}
                  onChange={(e) => setAmount(Number(e.target.value) || 0)}
                  disabled={phase !== 'form'}
                  className="w-full px-3 py-2.5 pr-16 rounded-lg border border-background-200/70 bg-background-50 text-sm font-semibold text-foreground-900 focus:outline-none focus:border-primary-300 disabled:opacity-60"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-foreground-400">{payCurrency}</span>
              </div>
            </div>

            {phase === 'form' && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Devise</label>
                  <select
                    value={payCurrency}
                    onChange={(e) => setPayCurrency(normalizeCurrency(e.target.value))}
                    className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.flag} {c.code} — {c.label}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-foreground-400 mt-1.5 leading-relaxed">
                    Devise de votre compte par défaut. Modifiez-la seulement si vous rechargez dans une autre devise.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="min-w-0">
                    <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Pays</label>
                    <select
                      value={country}
                      onChange={(e) => {
                        const c = e.target.value;
                        setCountry(c);
                        const first = resolveFeexPayNetworks(config).find((o) => o.enabled && o.country === c);
                        setNetwork(first?.slug || '');
                      }}
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                    >
                      {countries.length === 0 && <option value="">— À configurer —</option>}
                      {countries.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </select>
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Opérateur</label>
                    <select
                      value={network}
                      onChange={(e) => setNetwork(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                    >
                      {operators.length === 0 && <option value="">— Aucun opérateur —</option>}
                      {operators.map((o) => <option key={`${o.country}-${o.slug}`} value={o.slug}>{o.label}</option>)}
                    </select>
                  </div>
                </div>

                <div className="rounded-lg bg-secondary-50 border border-secondary-100 px-3 py-2.5">
                  <div className="flex items-center justify-between gap-3 text-xs">
                    <span className="text-foreground-600">Débité sur votre compte Mobile Money</span>
                    <span className="font-bold text-foreground-900 whitespace-nowrap">
                      {formatMoney(gatewayAmount)} {gatewayCurrency}
                    </span>
                  </div>
                  <p className="text-[11px] text-foreground-500 mt-1">
                    Conversion automatique : 1 {payCurrency} ≈ {rate.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {gatewayCurrency}.
                  </p>
                </div>
              </>
            )}

            {(phase === 'widget' || phase === 'verifying') && widget && (
              <div className="rounded-lg border border-background-200/70 bg-background-100/40 px-3 py-3">
                {phase === 'widget' ? (
                  <FeexPayModal
                    state={widget}
                    onResult={handleWidgetResult}
                    onFallback={() => { void verify(); }}
                  />
                ) : (
                  <div className="flex items-center gap-2 text-xs text-foreground-600">
                    <i className="ri-loader-4-line animate-spin text-primary-500"></i>
                    Confirmation du paiement…
                  </div>
                )}
              </div>
            )}

            {phase === 'preparing' && (
              <div className="flex items-center gap-2 text-xs text-foreground-600 px-1">
                <i className="ri-loader-4-line animate-spin text-primary-500"></i>
                Préparation du paiement…
              </div>
            )}

            {phase === 'pending' && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-foreground-700 leading-relaxed">
                <p className="font-semibold text-foreground-900 mb-1">Paiement en attente de confirmation</p>
                <p>{notice || 'Validez la demande reçue sur votre téléphone.'}</p>
                <button
                  type="button"
                  onClick={() => { void verify(lastTxnRef.current, widgetOkRef.current); }}
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-foreground-950 text-background-50 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800 transition-colors"
                >
                  <i className="ri-refresh-line"></i>
                  Vérifier à nouveau
                </button>
              </div>
            )}

            {error && (
              <div className="px-3 py-2.5 rounded-lg bg-red-50 text-red-600 text-xs flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span className="flex-1">{error}</span>
              </div>
            )}

            {phase === 'form' && (
              <button
                type="button"
                onClick={() => { void prepare(); }}
                className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-secure-payment-line"></i>
                Payer {amount ? `${amount.toLocaleString('fr-FR')} ${payCurrency}` : ''}
              </button>
            )}

            <p className="text-[11px] text-foreground-400 leading-relaxed text-center">
              Le montant est prélevé sur votre compte Mobile Money. Votre solde est crédité dès la confirmation du paiement.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}